/* Ombrance — motion.js : halo au pointeur, rail des séries, œuvre au mur (View Transition), et repli des
   révélations quand animation-timeline n'est pas pris en charge. Web Components natifs, aucune dépendance.
   Chargé en defer par chaque section qui en a besoin : le garde évite une double exécution. */

(() => {
  if (window.ombranceMotion) return;
  window.ombranceMotion = true;

  const doc = document;
  const animated = doc.body.dataset.animations === 'true';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const sda = Boolean(window.CSS && CSS.supports('animation-timeline: view()'));
  const throttle = (fn) => {
    let queued = false;
    return () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; fn(); });
    };
  };

  /* Repli : révélation unique à l'entrée dans la vue. Ce qui est déjà visible n'est jamais masqué.
     Désactivé dans l'éditeur de thème (les sections rechargées n'y seraient pas observées). */
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  if (animated && !sda && !reduce.matches && !designMode && 'IntersectionObserver' in window) {
    const root = doc.documentElement;
    const targets = [...doc.querySelectorAll('[data-reveal], [data-light]')];
    const limit = window.innerHeight * 0.85;
    targets.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < limit && r.bottom > 0) el.classList.add('is-in');
    });
    root.classList.add('no-sda', 'no-sda-init');
    requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('no-sda-init')));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -15% 0px', threshold: 0.2 });
    targets.forEach((el) => { if (!el.classList.contains('is-in')) io.observe(el); });
  }

  /* Halo de lumière : suit le pointeur dans son parent positionné (transform uniquement) */
  class LightHalo extends HTMLElement {
    connectedCallback() {
      this.zone = this.parentElement;
      if (!this.zone || !animated || reduce.matches || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      let x = 0;
      let y = 0;
      const paint = throttle(() => { this.style.transform = `translate3d(${x}px, ${y}px, 0)`; });
      this.onMove = (event) => {
        const rect = this.zone.getBoundingClientRect();
        x = event.clientX - rect.left;
        y = event.clientY - rect.top;
        paint();
      };
      this.onEnter = () => this.setAttribute('data-lit', '');
      this.onLeave = () => this.removeAttribute('data-lit');
      this.zone.addEventListener('pointermove', this.onMove, { passive: true });
      this.zone.addEventListener('pointerenter', this.onEnter);
      this.zone.addEventListener('pointerleave', this.onLeave);
    }
    disconnectedCallback() {
      if (!this.onMove) return;
      this.zone.removeEventListener('pointermove', this.onMove);
      this.zone.removeEventListener('pointerenter', this.onEnter);
      this.zone.removeEventListener('pointerleave', this.onLeave);
    }
  }

  /* Rail des séries : boutons précédent/suivant, bornes, progression (si timeline-scope n'est pas pris en charge) */
  class SeriesRail extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-rail-viewport]');
      if (!this.viewport) return;
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.bar = this.querySelector('[data-rail-bar]');
      this.cssProgress = animated && !reduce.matches && sda && CSS.supports('timeline-scope: --rail');
      this.sync = throttle(() => this.update());
      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.viewport.addEventListener('scroll', this.sync, { passive: true });
      window.addEventListener('resize', this.sync);
      this.update();
    }
    disconnectedCallback() { window.removeEventListener('resize', this.sync); }
    step() {
      const item = this.viewport.querySelector('li');
      if (!item) return this.viewport.clientWidth * 0.8;
      return item.offsetWidth + (parseFloat(getComputedStyle(item.parentElement).columnGap) || 0);
    }
    go(direction) {
      this.viewport.scrollBy({ left: direction * this.step(), behavior: reduce.matches ? 'auto' : 'smooth' });
    }
    update() {
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      const x = Math.abs(this.viewport.scrollLeft);
      if (this.prev) this.prev.disabled = x < 4;
      if (this.next) this.next.disabled = x > max - 4;
      if (this.bar && !this.cssProgress) this.bar.style.setProperty('--p', (0.18 + 0.82 * (max > 0 ? x / max : 1)).toFixed(3));
    }
  }

  /* Œuvre au mur : changement en fondu « ombre → lumière » (View Transition), image décodée avant la capture */
  class RoomPreview extends HTMLElement {
    connectedCallback() {
      this.img = this.querySelector('[data-room-img]');
      this.art = this.querySelector('[data-room-art]');
      this.caption = this.querySelector('[data-room-caption]');
      this.name = this.querySelector('[data-room-name]');
      this.link = this.querySelector('[data-room-link]');
      this.choices = [...this.querySelectorAll('[data-art]')];
      this.choices.forEach((button) => button.addEventListener('click', () => this.select(button)));
    }
    async select(button) {
      if (!this.img || button.getAttribute('aria-pressed') === 'true') return;
      const { src, srcset, alt, name, url } = button.dataset;
      const next = new Image();
      next.sizes = this.img.sizes;
      next.srcset = srcset;
      next.src = src;
      try { await next.decode(); } catch (e) { /* l'image s'affichera à son chargement */ }
      const apply = () => {
        this.choices.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
        this.img.loading = 'eager';
        this.img.srcset = srcset;
        this.img.src = src;
        this.img.alt = alt;
        if (this.name) this.name.textContent = name;
        if (this.link && url) this.link.href = url;
      };
      if (!doc.startViewTransition || !animated || reduce.matches || !this.art) { apply(); return; }
      this.art.style.viewTransitionName = 'room-art';
      if (this.caption) this.caption.style.viewTransitionName = 'room-caption';
      const transition = doc.startViewTransition(apply);
      transition.finished.finally(() => {
        this.art.style.viewTransitionName = '';
        if (this.caption) this.caption.style.viewTransitionName = '';
      });
    }
  }

  if (!customElements.get('light-halo')) customElements.define('light-halo', LightHalo);
  if (!customElements.get('series-rail')) customElements.define('series-rail', SeriesRail);
  if (!customElements.get('room-preview')) customElements.define('room-preview', RoomPreview);
})();
